import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCw } from 'lucide-react';
import { Refresh } from 'iconoir-react';

import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { SparkleText } from 'features/chat/agent-chat-module/utils/chat-event-messages';
import { useAgentPreviewChat } from 'features/chat/hooks/use-agent-preview-chat';
import { StreamStatusTimeline } from 'features/chat/components/StreamStatusTimeline';
import { McpServiceAuthModal } from 'features/chat/components/McpServiceAuthModal';
import { cn } from 'lib/utils';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';
import type { ChatbotWidget } from './index.type';
import { DateSeparator, PreviewChatItem } from './preview-chat-item';
import { PreviewChatInput } from './preview-chat-input';
import { PreviewChatItemSuggestions } from './preview-chat-item-suggestions';

type PreviewChatbotProps = {
  agentId: string;
  widgetId: string;
  projectKey: string;
  widget: ChatbotWidget;
  humanLogoUrl?: string;
  lastUpdatedDate?: string;
  isSaving?: boolean;
  lastSaveTime?: Date | null;
  onResetChat?: () => void;
  /** When true, header/chat/input use widget designSettings colors; otherwise theme defaults. */
  applyWidgetColors?: boolean;
  /** Figma edit-agent preview: black header, light/dark chat body. */
  variant?: 'default' | 'figma-edit';
  className?: string;
};

export type { PreviewChatbotProps };

export function PreviewChatbot({
  agentId,
  widgetId,
  projectKey,
  widget,
  onResetChat,
  applyWidgetColors = false,
  variant = 'default',
  className,
}: PreviewChatbotProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    conversations,
    conversationId,
    suggestions,
    streamStatusEntries,
    isBotThinking,
    isBotStreaming,
    isResponseGenerating,
    isConnected,
    sendMessage,
  } = useAgentPreviewChat({ agentId, widgetId });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.scrollTo?.({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [conversations, suggestions, isBotThinking, isBotStreaming]);

  const shouldShowDateSeparator = (currentTimestamp?: string, previousTimestamp?: string) => {
    if (!currentTimestamp) return false;
    if (!previousTimestamp) return true;

    return new Date(currentTimestamp).toDateString() !== new Date(previousTimestamp).toDateString();
  };

  const chatHeaderStyle = useMemo(() => {
    if (!applyWidgetColors) {
      return undefined;
    }

    const ds = widget.designSettings;
    const start = ds.headerStartColor?.trim() || '#000000';
    const end = ds.headerEndColor?.trim() || '#000000';
    const titleColor = ds.titleColor?.trim() || '#FFFFFF';

    return {
      backgroundImage: `linear-gradient(90deg, ${start}, ${end})`,
      color: titleColor,
    };
  }, [applyWidgetColors, widget.designSettings]);

  const chatBodyStyle = useMemo(() => {
    if (!applyWidgetColors) {
      return undefined;
    }

    return {
      backgroundColor: widget.designSettings.chatBackgroundColor || undefined,
    };
  }, [applyWidgetColors, widget.designSettings]);

  const chatFontColor = applyWidgetColors
    ? widget.designSettings.chatFontColor?.trim() || '#000000'
    : undefined;

  const isFigmaEdit = variant === 'figma-edit';
  const displayName = widget.name || t('SYSTEM', { defaultValue: 'System' });
  const nameInitial = displayName.trim().charAt(0).toUpperCase() || 'A';

  const messageArea = (
    <>
      <div className={isFigmaEdit ? 'flex w-full items-start px-[18px] pt-4' : undefined}>
        <PreviewChatItem
          message={widget.greeting}
          isMarkedDown
          showAgentName={false}
          fontColor={chatFontColor}
        />
      </div>

      {conversations.map((item, index) => {
        const previousItem = conversations[index - 1];
        const showDateSeparator = shouldShowDateSeparator(item.timestamp, previousItem?.timestamp);
        const messageColor = chatFontColor;
        const isLatestBot =
          item.type === 'bot' &&
          !conversations.slice(index + 1).some((next) => next.type === 'bot');

        return (
          <div
            key={item.id}
            className={cn('min-w-0 w-full max-w-full', isFigmaEdit && 'px-[18px]')}
          >
            {showDateSeparator && item.timestamp ? (
              <DateSeparator date={item.timestamp} fontColor={messageColor} />
            ) : null}
            <PreviewChatItem
              name={item.type === 'bot' ? widget.name || t('ASSISTANT') : t('YOU')}
              message={item.message}
              isMarkedDown={item.type === 'bot'}
              isBot={item.type === 'bot'}
              timestamp={item.timestamp}
              showAgentName={false}
              fontColor={messageColor}
              a2uiSurfaces={item.a2uiSurfaces}
              conversationId={conversationId}
              userMessageId={item.userRecordId}
              assistantMessageId={item.messageId}
              isLatest={isLatestBot}
            />
          </div>
        );
      })}

      {isBotThinking && (
        <div
          className={cn(
            'duration-500 animate-in fade-in slide-in-from-left-2',
            isFigmaEdit && 'px-[18px]',
          )}
        >
          <SparkleText text={t('SENDING', { defaultValue: 'Sending...' })} />
          {(streamStatusEntries?.length ?? 0) > 0 && (
            <StreamStatusTimeline entries={streamStatusEntries} />
          )}
        </div>
      )}

      {suggestions.length > 0 ? (
        <div className={isFigmaEdit ? 'px-[18px]' : undefined}>
          <PreviewChatItemSuggestions suggestions={suggestions} onSelect={sendMessage} />
        </div>
      ) : null}
    </>
  );

  return (
    <div
      id={`chatbot-container-${widgetId}`}
      className={cn(
        'flex h-full w-full flex-col items-start',
        isFigmaEdit && cn('overflow-hidden rounded-2xl border', ui.chipWrap),
        className,
      )}
    >
      <div
        data-testid="preview-chatbot-header"
        className={
          isFigmaEdit
            ? cn(
                'flex w-full shrink-0 items-center gap-3 px-4 py-3',
                applyWidgetColors
                  ? 'text-true-white'
                  : 'bg-black text-true-white dark:bg-[#171717]'
              )
            : applyWidgetColors
              ? 'sticky top-0 z-10 flex w-full shrink-0 items-center justify-between px-4 py-3'
              : 'sticky top-0 z-10 flex w-full shrink-0 items-center justify-between bg-primary px-4 py-3 text-primary-foreground'
        }
        style={chatHeaderStyle}
      >
        {isFigmaEdit ? (
          <div
            className="flex size-8 shrink-0 items-center justify-center rounded-full border border-white bg-white/15 text-xs font-semibold text-true-white"
            data-testid="preview-chatbot-avatar"
            aria-hidden
          >
            {nameInitial}
          </div>
        ) : null}
        <h4
          className={
            isFigmaEdit
              ? 'min-w-0 flex-1 truncate text-sm font-semibold text-true-white'
              : 'min-w-0 flex-1 truncate pr-2 text-sm font-semibold'
          }
          style={chatHeaderStyle ? { color: chatHeaderStyle.color } : undefined}
          title={displayName}
        >
          {displayName}
        </h4>
        {onResetChat ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={onResetChat}
                  className={
                    isFigmaEdit
                      ? 'size-8 rounded-full text-true-white hover:bg-white/10 hover:text-true-white'
                      : 'h-8 w-8 rounded-full text-inherit hover:bg-black/10'
                  }
                  style={chatHeaderStyle ? { color: chatHeaderStyle.color } : undefined}
                >
                  {isFigmaEdit ? (
                    <Refresh width={16} height={16} strokeWidth={1.5} />
                  ) : (
                    <RotateCw className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('REFRESH')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>

      {isFigmaEdit ? (
        <div
          className="flex min-h-0 w-full flex-1 flex-col justify-between px-3 pb-3 pt-2"
          style={chatBodyStyle}
        >
          <div
            ref={containerRef}
            className={cn(
              'flex min-h-0 w-full flex-1 flex-col overflow-x-hidden overflow-y-auto text-sm font-medium leading-[1.6]',
              !applyWidgetColors && ui.body,
            )}
            style={chatBodyStyle}
          >
            {messageArea}
          </div>

          <PreviewChatInput
            config={widget.designSettings}
            applyWidgetColors={applyWidgetColors}
            variant={variant}
            onSubmit={sendMessage}
            disable={!isConnected || !agentId || isResponseGenerating}
          />
        </div>
      ) : (
        <>
          <div
            ref={containerRef}
            className={
              applyWidgetColors
                ? 'flex min-h-0 w-full flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto border-x px-4 py-3 text-base font-normal'
                : 'flex min-h-0 w-full flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto border-x bg-background px-4 py-3 text-base font-normal text-foreground'
            }
            style={chatBodyStyle}
          >
            {messageArea}
          </div>

          <div className="w-full shrink-0">
            <PreviewChatInput
              config={widget.designSettings}
              applyWidgetColors={applyWidgetColors}
              variant={variant}
              onSubmit={sendMessage}
              disable={!isConnected || !agentId || isResponseGenerating}
            />
          </div>
        </>
      )}
      <McpServiceAuthModal />
    </div>
  );
}
